import { Backdrop, CircularProgress } from "@mui/material";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Edit,
  Folder,
  X,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../../API/newIndex";
import { GenerateSlugName } from "../../controllers/GenerateSlugName";
import { clientData } from "../../controllers/GetClient";
import UnsavedChangesModal from "../../controllers/UnsavedChangesModal";
import { useRoutingByType } from "../../controllers/useRouting";
import useUnsavedChanges from "../../controllers/useUnsavedChanges";
import { uploadTextEditorImage } from "../../utilities/staticFunction";
import AiTextGenerate from "../AiTextGenerate";
import { ButtonComponent, DetailsComponent, InputComponent, SelectComponent, RichTextEditor } from '@shopeasy-dashboard-ui/ui-library';
import Notification from "../Notification";

const getChangedFields = (currentValues, originalValues) => {
  if (!currentValues || !originalValues) return currentValues || {};
  const changed = {};
  Object.keys(currentValues).forEach((key) => {
    const cur = currentValues[key];
    const orig = originalValues[key];
    if (JSON.stringify(cur) !== JSON.stringify(orig)) {
      changed[key] = cur;
    }
  });
  return changed;
};

const BulkEditPortfolioCategory = ({
  isBulkEdit = true,
  bulkCategoryIds = [],
  onClose,
  onSuccess,
}) => {
  const [bulkCategoriesList, setBulkCategoriesList] = useState([]);
  const [activeBulkIndex, setActiveBulkIndex] = useState(0);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const bulkCategoriesDataRef = useRef({});

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [btnLoading, setBtnLoading] = useState(false);
  const [formSpinner, setFormSpinner] = useState(false);
  const [AiLoader, setAiloader] = useState(false);

  // Form State for Active Category Item
  const [warn, setWarn] = useState(false);
  const [enableSlug, setEnableSlug] = useState(false);
  const slugInputRef = useRef(null);

  const [portfolioCatData, setPortfolioCatData] = useState({
    portfoliocategory_name: "",
    meta_tag_title: "",
    meta_tag_description: "",
    keyword: "",
  });
  const [description, setDescription] = useState("");
  const [selectedParentCategory, setSelectedParentCategory] = useState("");
  const [slugName, setSlugName] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [isMetaTitleCustom, setIsMetaTitleCustom] = useState(false);

  const { portfoliocategory_name, meta_tag_title, meta_tag_description, keyword } = portfolioCatData;

  const originalDataRef = useRef(null);
  const originalPayloadRef = useRef(null);

  const [ParentCategory, setParentCategory] = useState([]);
  const portfolioRoute = useRoutingByType("portfolio");
  const protocol = process.env.REACT_APP_ENVIRONMENT === "DEV" ? "http://" : "https://";
  const clientInfo = clientData;

  const HandleInput = (e) => {
    const { name, value } = e.target;
    if (name === "meta_tag_title") {
      setIsMetaTitleCustom(Boolean(value));
    }
    setPortfolioCatData((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = (formData = {}) => {
    return {
      portfoliocategory_name: formData.portfoliocategory_name || "",
      portfolio_category_slug_name: formData.slugName || "",
      description: formData.description || "",
      parent_portfoliocategory: formData.selectedParentCategory || null,
      meta_tag_title: formData.meta_tag_title || "",
      meta_tag_description: formData.meta_tag_description || "",
      keyword: formData.keyword || "",
    };
  };

  const currentFormValues = useMemo(() => {
    return {
      portfoliocategory_name,
      slugName,
      description,
      selectedParentCategory,
      meta_tag_title,
      meta_tag_description,
      keyword,
    };
  }, [
    portfoliocategory_name,
    slugName,
    description,
    selectedParentCategory,
    meta_tag_title,
    meta_tag_description,
    keyword,
  ]);

  const GetParentCategory = async () => {
    try {
      const res = await API({
        url: `/admin/portfoliocategory/GetPortfolioCategoriesWithParent?default=true`,
        method: "get",
        params: { limit: "all" },
      });
      if (res.status === 200 || res.status === 304) {
        setParentCategory(res?.data?.data?.records || []);
      }
    } catch (error) {
      console.error("Error fetching parent categories:", error);
    }
  };

  useEffect(() => {
    GetParentCategory();
  }, []);

  const handleSlug = async (slug) => {
    if (slug?.length) {
      const generated = await GenerateSlugName(slug);
      setSlugName(generated);
    } else {
      setSlugName("");
    }
  };

  const applyCategoryToForm = (rawCat) => {
    if (!rawCat) return;
    const parentId = rawCat?.parent_portfoliocategory
      ? (typeof rawCat.parent_portfoliocategory === "object"
        ? rawCat.parent_portfoliocategory._id || rawCat.parent_portfoliocategory.id
        : String(rawCat.parent_portfoliocategory))
      : "";

    const initialFormValues = {
      portfoliocategory_name: rawCat?.portfoliocategory_name || rawCat?.categoryName || "",
      slugName: rawCat?.portfolio_category_slug_name || rawCat?.slug || "",
      description: rawCat?.description || "",
      selectedParentCategory: parentId,
      meta_tag_title: rawCat?.meta_tag_title || "",
      meta_tag_description: rawCat?.meta_tag_description || "",
      keyword: rawCat?.keyword || "",
    };

    originalDataRef.current = initialFormValues;
    originalPayloadRef.current = buildPayload(initialFormValues);

    setPortfolioCatData({
      portfoliocategory_name: initialFormValues.portfoliocategory_name,
      meta_tag_title: initialFormValues.meta_tag_title,
      meta_tag_description: initialFormValues.meta_tag_description,
      keyword: initialFormValues.keyword,
    });
    setIsMetaTitleCustom(Boolean(rawCat?.meta_tag_title));
    setSlugName(initialFormValues.slugName);
    setSlugEdited(Boolean(initialFormValues.slugName));
    setDescription(initialFormValues.description);
    setSelectedParentCategory(initialFormValues.selectedParentCategory);
    setWarn(false);
    setEnableSlug(false);
  };

  const fetchCategoriesByIds = async () => {
    if (!bulkCategoryIds || bulkCategoryIds.length === 0) return;
    setFormSpinner(true);
    try {
      const res = await API({
        url: "/admin/portfoliocategory/GetPortfolioCategoryDataByIds",
        method: "post",
        data: {
          categories: bulkCategoryIds,
          ids: bulkCategoryIds,
          portfoliocategories: bulkCategoryIds,
          id: bulkCategoryIds,
        },
      });

      let loadedRecords = [];
      if (res?.status === 200 || res?.status === 304) {
        const resultData = res?.data;
        loadedRecords =
          resultData?.data?.records ||
          resultData?.records ||
          (Array.isArray(resultData?.data) ? resultData.data : []) ||
          (Array.isArray(resultData) ? resultData : []);
      }

      if (loadedRecords.length === 0) {
        const individualPromises = bulkCategoryIds.map((id) =>
          API({
            url: `/admin/portfoliocategory/getPortfolioCategory/${id}`,
            method: "get",
          })
            .then((r) => r?.data?.data)
            .catch(() => null)
        );
        const results = await Promise.all(individualPromises);
        loadedRecords = results.filter(Boolean);
      }

      setBulkCategoriesList(loadedRecords);

      const initialCache = {};
      loadedRecords.forEach((item) => {
        initialCache[item._id] = {
          raw: item,
          values: {
            portfoliocategory_name: item?.portfoliocategory_name || item?.categoryName || "",
            slugName: item?.portfolio_category_slug_name || item?.slug || "",
            description: item?.description || "",
            selectedParentCategory: item?.parent_portfoliocategory
              ? (typeof item.parent_portfoliocategory === "object"
                ? item.parent_portfoliocategory._id || item.parent_portfoliocategory.id
                : String(item.parent_portfoliocategory))
              : "",
            meta_tag_title: item?.meta_tag_title || "",
            meta_tag_description: item?.meta_tag_description || "",
            keyword: item?.keyword || "",
          },
          original: {
            portfoliocategory_name: item?.portfoliocategory_name || item?.categoryName || "",
            slugName: item?.portfolio_category_slug_name || item?.slug || "",
            description: item?.description || "",
            selectedParentCategory: item?.parent_portfoliocategory
              ? (typeof item.parent_portfoliocategory === "object"
                ? item.parent_portfoliocategory._id || item.parent_portfoliocategory.id
                : String(item.parent_portfoliocategory))
              : "",
            meta_tag_title: item?.meta_tag_title || "",
            meta_tag_description: item?.meta_tag_description || "",
            keyword: item?.keyword || "",
          },
          isModified: false,
        };
      });
      bulkCategoriesDataRef.current = initialCache;

      if (loadedRecords.length > 0) {
        applyCategoryToForm(loadedRecords[0]);
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error?.message || "Failed to load categories for bulk edit");
      setSeverity("error");
    } finally {
      setFormSpinner(false);
    }
  };

  useEffect(() => {
    fetchCategoriesByIds();
  }, [bulkCategoryIds]);

  const activeCategory = bulkCategoriesList[activeBulkIndex] || null;

  const isCategoryModified = (item, index) => {
    if (!item) return false;
    if (index === activeBulkIndex) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || (originalDataRef.current ? buildPayload(originalDataRef.current) : null);
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      return Object.keys(changed).length > 0;
    }
    return Boolean(bulkCategoriesDataRef.current[item._id]?.isModified);
  };

  const modifiedCount = useMemo(() => {
    return bulkCategoriesList.reduce((acc, item, idx) => {
      return acc + (isCategoryModified(item, idx) ? 1 : 0);
    }, 0);
  }, [bulkCategoriesList, activeBulkIndex, currentFormValues]);

  const switchActiveCategory = (newIndex) => {
    if (newIndex < 0 || newIndex >= bulkCategoriesList.length) return;
    if (newIndex === activeBulkIndex) return;

    const currentCategory = bulkCategoriesList[activeBulkIndex];
    if (currentCategory) {
      const currentPayload = buildPayload(currentFormValues);
      const baseline = originalPayloadRef.current || (originalDataRef.current ? buildPayload(originalDataRef.current) : null);
      const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
      const isModified = Object.keys(changed).length > 0;

      bulkCategoriesDataRef.current[currentCategory._id] = {
        values: { ...currentFormValues },
        original: originalDataRef.current,
        originalPayload: originalPayloadRef.current,
        isModified,
      };
    }

    setActiveBulkIndex(newIndex);
    const nextCategory = bulkCategoriesList[newIndex];
    if (!nextCategory) return;

    const saved = bulkCategoriesDataRef.current[nextCategory._id];
    if (saved) {
      originalDataRef.current = saved.original;
      originalPayloadRef.current = saved.originalPayload;

      setPortfolioCatData({
        portfoliocategory_name: saved.values.portfoliocategory_name,
        meta_tag_title: saved.values.meta_tag_title,
        meta_tag_description: saved.values.meta_tag_description,
        keyword: saved.values.keyword,
      });
      setIsMetaTitleCustom(Boolean(saved ? saved.values.meta_tag_title : nextCategory?.meta_tag_title));
      setSlugName(saved.values.slugName);
      setSlugEdited(Boolean(saved.values.slugName));
      setDescription(saved.values.description);
      setSelectedParentCategory(saved.values.selectedParentCategory);
      setWarn(false);
      setEnableSlug(false);
    } else {
      applyCategoryToForm(nextCategory);
    }
  };

  const resetAndClose = useCallback(() => {
    bulkCategoriesDataRef.current = {};
    if (onClose) onClose();
  }, [onClose]);

  const {
    showUnsavedModal,
    handleRequestClose: handleBulkEditCancel,
    handleConfirmLeave,
    handleStay,
  } = useUnsavedChanges(modifiedCount > 0, resetAndClose);

  const activeCategoryName = useMemo(() => {
    const active = bulkCategoriesList[activeBulkIndex];
    return portfoliocategory_name || active?.portfoliocategory_name || active?.categoryName || "Category";
  }, [bulkCategoriesList, activeBulkIndex, portfoliocategory_name]);

  const filteredCategoriesList = useMemo(() => {
    if (!categorySearchTerm.trim()) return bulkCategoriesList;
    return bulkCategoriesList.filter((c) =>
      (c.portfoliocategory_name || c.parent_category || c.categoryName || "")
        .toLowerCase()
        .includes(categorySearchTerm.toLowerCase())
    );
  }, [bulkCategoriesList, categorySearchTerm]);

  const generateMetaData = async (sourceText, type) => {
    const sourceContent = sourceText?.replace(/<[^>]*>/g, " ").trim() || portfoliocategory_name || "";
    if (!sourceContent) {
      setIsNotification(true);
      setNotificationMsg(t("lang_Please_Enter_Title") || "Please enter title or description first");
      setSeverity("warning");
      return;
    }

    setAiloader(true);
    try {
      const payload = { name: sourceContent };
      if (type === "description") {
        const res = await API({
          url: "/admin/ai/category_meta_description",
          method: "post",
          data: payload,
        });
        setPortfolioCatData((prev) => ({
          ...prev,
          meta_tag_description: res?.data?.data || "",
        }));
      } else {
        const res = await API({
          url: "/admin/ai/category_meta_title",
          method: "post",
          data: payload,
        });
        setIsMetaTitleCustom(true);
        setPortfolioCatData((prev) => ({
          ...prev,
          meta_tag_title: res?.data?.data || "",
        }));
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error?.message || "AI metadata generation failed");
      setSeverity("error");
    } finally {
      setAiloader(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!portfoliocategory_name || !portfoliocategory_name.trim()) {
      setWarn(true);
      setIsNotification(true);
      setNotificationMsg("Portfolio Category Name is required!");
      setSeverity("error");
      return;
    }

    try {
      setBtnLoading(true);
      setFormSpinner(true);

      const currentCategory = bulkCategoriesList[activeBulkIndex];
      if (currentCategory) {
        const currentPayload = buildPayload(currentFormValues);
        const baseline = originalPayloadRef.current || (originalDataRef.current ? buildPayload(originalDataRef.current) : null);
        const changed = baseline ? getChangedFields(currentPayload, baseline) : {};
        const isModified = Object.keys(changed).length > 0;

        bulkCategoriesDataRef.current[currentCategory._id] = {
          values: { ...currentFormValues },
          original: originalDataRef.current,
          originalPayload: originalPayloadRef.current,
          isModified,
        };
      }

      const modifiedEntries = Object.entries(bulkCategoriesDataRef.current).filter(
        ([_, data]) => data?.isModified
      );

      if (modifiedEntries.length === 0) {
        setIsNotification(true);
        setNotificationMsg("No changes detected to update");
        setSeverity("info");
        setBtnLoading(false);
        setFormSpinner(false);
        return;
      }

      for (const [id, data] of modifiedEntries) {
        if (!data.values.portfoliocategory_name || !data.values.portfoliocategory_name.trim()) {
          setIsNotification(true);
          setNotificationMsg("All modified Categories must have a Name");
          setSeverity("error");
          setBtnLoading(false);
          setFormSpinner(false);
          return;
        }
      }

      const bulkPayload = modifiedEntries
        .map(([id, data]) => {
          const payload = buildPayload(data.values);
          return {
            _id: id,
            ...payload,
          };
        })
        .filter(Boolean);

      const res = await API({
        url: "/admin/portfoliocategory/BulkUpdatePortfolioCategory",
        method: "post",
        data: bulkPayload,
      });

      if (res.status === 200 || res.status === 304) {
        setIsNotification(true);
        setNotificationMsg(
          `${bulkPayload.length} categor${bulkPayload.length > 1 ? "ies" : "y"} updated successfully!`
        );
        setSeverity("success");
        bulkCategoriesDataRef.current = {};
        if (onSuccess) {
          onSuccess(bulkPayload.length);
        }
      } else {
        throw new Error(res?.data?.message || "Failed to update categories");
      }
    } catch (error) {
      console.error("Error bulk updating categories:", error);
      setIsNotification(true);
      setNotificationMsg(error?.response?.data?.message || error?.message || "Failed to update categories");
      setSeverity("error");
    } finally {
      setBtnLoading(false);
      setFormSpinner(false);
    }
  };

  const availableParentCategories = useMemo(() => {
    if (!activeCategory) return ParentCategory;
    return ParentCategory.filter((x) => (x._id || x.id) !== activeCategory._id);
  }, [ParentCategory, activeCategory]);

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] font-sans">
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      {(formSpinner || AiLoader) && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={formSpinner || AiLoader}
          className="flex flex-col"
        >
          <CircularProgress color="inherit" />
          <p className="mt-2 text-sm">
            {AiLoader ? t("lang_Please_Wait") || "Generating content..." : t("lang_loading") || "Processing..."}
          </p>
        </Backdrop>
      )}

      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--border-color)] bg-[var(--secondary-bg-color)] shrink-0">
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleBulkEditCancel}
            className="w-7 h-7 rounded-full flex items-center justify-center text-[var(--table-text-color)] hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)] transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="!text-sm font-bold text-[var(--primary-text-color)] !m-0 leading-none">
                Bulk Edit Portfolio Categories
              </span>
              <span className="bg-[var(--focus-tab-color)] text-[var(--primary-text-color)] text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--border-color)] leading-none">
                {bulkCategoriesList.length} Selected
              </span>
            </div>
            <p className="!text-[11px] text-[var(--table-text-color)] !m-0 !mt-1">
              Select any category from the list to view and edit its details.
            </p>
          </div>
        </div>
      </div>

      {/* Main Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Sidebar Panel */}
        <div className="w-[300px] border-r border-[var(--border-color)] bg-[var(--primary-bg-color)] p-4 flex flex-col shrink-0">
          {/* Outer Rounded Container */}
          <div className="flex-1 flex flex-col border border-[var(--border-color)] rounded-2xl bg-[var(--secondary-bg-color)] overflow-hidden shadow-[var(--shadow-card)]">
            {/* Header section */}
            <div className="p-3.5 border-b border-[var(--border-color)] flex items-center justify-between">
              <span className="text-xs font-bold text-[var(--primary-text-color)]">
                Selected Categories
              </span>
              <div className="flex items-center space-x-1 text-xs text-[var(--table-text-color)]">
                <button
                  type="button"
                  disabled={activeBulkIndex === 0}
                  onClick={() => switchActiveCategory(activeBulkIndex - 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="font-mono text-[11px] text-[var(--table-text-color)]">
                  {activeBulkIndex + 1}/{bulkCategoriesList.length}
                </span>
                <button
                  type="button"
                  disabled={activeBulkIndex === bulkCategoriesList.length - 1}
                  onClick={() => switchActiveCategory(activeBulkIndex + 1)}
                  className="p-1 rounded hover:bg-[var(--focus-tab-color)] disabled:opacity-30 disabled:pointer-events-none cursor-pointer text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Filter Search Input Section */}
            <div className="p-3 border-b border-[var(--border-color)] bg-transparent">
              <input
                type="text"
                placeholder="Filter selected categories..."
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-[var(--border-color)] rounded-lg bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] placeholder-[var(--table-text-color)] focus:outline-none focus:border-[var(--focus-border-color)] shadow-xs"
              />
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filteredCategoriesList.map((cat) => {
                const actualIndex = bulkCategoriesList.findIndex((item) => item._id === cat._id);
                const isActive = actualIndex === activeBulkIndex;
                const isModified = isCategoryModified(cat, actualIndex);
                const displayName =
                  isActive
                    ? (portfoliocategory_name || cat.portfoliocategory_name || cat.categoryName || "-")
                    : (cat.portfoliocategory_name || cat.categoryName || "-");
                const parentLabel = cat.parent_category || "Top Level";

                return (
                  <div
                    key={cat._id}
                    onClick={() => switchActiveCategory(actualIndex)}
                    className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 border-b border-[var(--border-color)] last:border-b-0 ${isActive
                      ? "bg-[var(--focus-tab-color)]"
                      : "bg-[var(--secondary-bg-color)] hover:bg-[var(--focus-tab-color)]"
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-md bg-[var(--primary-bg-color)] border border-[var(--border-color)] flex items-center justify-center shrink-0 overflow-hidden p-1 text-[var(--table-text-color)]">
                        <Folder size={18} />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className={`text-xs truncate ${isActive
                            ? "font-bold text-[var(--primary-text-color)]"
                            : "font-medium text-[var(--primary-text-color)]"
                            }`}
                        >
                          {displayName}
                        </span>
                        <span className="text-[11px] text-[var(--table-text-color)] truncate mt-0.5 capitalize">
                          {parentLabel}
                        </span>
                      </div>
                    </div>

                    {isModified && (
                      <span
                        title='Modified'
                        className={`w-2 h-2 rounded-full bg-amber-500 shrink-0 select-none inline-block`}
                      />
                    )}
                  </div>
                );
              })}

              {filteredCategoriesList.length === 0 && (
                <div className="p-4 text-center text-xs text-[var(--table-text-color)]">
                  No categories matching &quot;{categorySearchTerm}&quot;
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Main Form Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-[var(--primary-bg-color)]">
          {/* Right Sub-Header Bar */}
          <div className="flex items-center justify-between px-6 py-2.5 bg-[var(--secondary-bg-color)] border-b border-[var(--border-color)] text-xs shrink-0">
            <div>
              <span className="font-bold text-[var(--primary-text-color)]">
                Currently Editing: {activeCategoryName}
              </span>{" "}
              <span className="text-[var(--table-text-color)] font-mono text-[11px]">
                ({activeBulkIndex + 1} of {bulkCategoriesList.length})
              </span>
            </div>
            <div>
              {modifiedCount > 0 ? (
                <span className="text-emerald-500 font-medium text-xs">
                  {modifiedCount} category modified
                </span>
              ) : (
                <span className="text-[var(--table-text-color)] text-xs font-normal">
                  No unsaved changes
                </span>
              )}
            </div>
          </div>

          {/* Form Area matching AddPCategory.js */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            <form
              onKeyDown={(e) => e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.preventDefault()}
              className="max-w-4xl mx-auto flex flex-col gap-4 pb-12"
            >
              {/* Card 1: Category Name, URL Slug, Parent Category & Description */}
              <DetailsComponent
                open={true}
                hasHr={false}
                showChevron={false}
                className="!pt-5 sm:!pt-[25px]"
              >
                <div className="w-full mb-4">
                  <label htmlFor="portfoliocategory_name" className="flex items-center justify-between">
                    <p className="text-[13px] font-medium text-[var(--primary-text-color)] m-0">
                      {t("lang_Portfolio_Category") || "Portfolio Category Name"}
                      <span className="text-red-500 ms-1">*</span>
                    </p>
                  </label>
                  <InputComponent
                    className={warn ? `border-red-500` : ``}
                    type="text"
                    name="portfoliocategory_name"
                    value={portfoliocategory_name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setWarn(false);
                      if (!enableSlug && (!slugEdited || !slugName)) handleSlug(newName);
                      const shouldSyncMeta = !isMetaTitleCustom;
                      setPortfolioCatData((prev) => ({
                        ...prev,
                        portfoliocategory_name: newName,
                        ...(shouldSyncMeta ? { meta_tag_title: newName } : {}),
                      }));
                    }}
                    placeholder="Enter category name"
                    onBlur={() => {
                      if (!enableSlug && (!slugEdited || !slugName) && portfoliocategory_name) handleSlug(portfoliocategory_name);
                      if (!isMetaTitleCustom && !meta_tag_title && portfoliocategory_name) {
                        setPortfolioCatData((prev) => ({ ...prev, meta_tag_title: portfoliocategory_name }));
                      }
                    }}
                  />
                  <div className="flex items-center justify-between mt-1 text-xs text-gray-500 w-full gap-1 min-h-[24px]">
                    <div className="flex items-center w-[90%] min-w-0">
                      <a
                        href={`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${portfolioRoute}/category/${(slugName || '').toLowerCase()}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium shrink-0 mr-2 hover:underline cursor-pointer text-blue-600 flex items-center"
                      >
                        Preview
                      </a>
                      {/* INLINE EDIT */}
                      {!enableSlug ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate text-gray-500 min-w-0">
                            {`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${portfolioRoute}/category/${(slugName || '').toLowerCase()}`}
                          </span>

                          <button
                            type="button"
                            onClick={() => {
                              setEnableSlug(true);
                              setTimeout(() => slugInputRef.current?.focus(), 50);
                            }}
                            className="text-gray-600 hover:text-black shrink-0 flex items-center cursor-pointer"
                          >
                            <Edit size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <span className="text-gray-400 shrink truncate max-w-[120px] sm:max-w-[200px] md:max-w-[300px] lg:max-w-[400px]">
                            {`${protocol}${clientInfo?.custom_domain || clientInfo?.customer_domain}${portfolioRoute}/category/`}
                          </span>

                          <InputComponent
                            ref={slugInputRef}
                            name="portfolio_category_slug_name"
                            className="flex-1 min-w-[60px] !h-[22px] !py-0.5 !px-1 text-xs"
                            value={slugName || ''}
                            onChange={(e) => {
                              setSlugName(e.target.value);
                              setSlugEdited(Boolean(e.target.value));
                            }}
                            onBlur={async () => {
                              if (slugName) {
                                const generated = await GenerateSlugName(slugName);
                                setSlugName(generated);
                              }
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (!slugName) {
                                  setIsNotification(true);
                                  setSeverity("warning");
                                  setNotificationMsg("Slug can not be empty!");
                                  return;
                                }
                                const generated = await GenerateSlugName(slugName);
                                setSlugName(generated);
                                setEnableSlug(false);
                                setSlugEdited(true);
                              }
                            }}
                            isSlug={true}
                          />

                          <button
                            type="button"
                            className="text-green-600 ml-1 shrink-0 flex items-center cursor-pointer"
                            onClick={async () => {
                              if (!slugName) {
                                setIsNotification(true);
                                setSeverity("warning");
                                setNotificationMsg("Slug can not be empty!");
                                return;
                              }

                              const generated = await GenerateSlugName(slugName);
                              setSlugName(generated);
                              setEnableSlug(false);
                              setSlugEdited(true);
                            }}
                          >
                            <Check size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    <span className={`shrink-0 text-end w-[10%] flex items-center justify-end ${(portfoliocategory_name?.length || 0) > 60 ? 'text-red-500 font-semibold' : ''}`}>
                      {portfoliocategory_name?.length || 0}/60
                    </span>
                  </div>
                  {warn && (
                    <p className="text-red-500 text-xs mt-1">{t("lang_Please_Enter_Category") || "Please enter category name"}</p>
                  )}
                </div>

                {/* Parent Category */}
                <div className="mb-4">
                  <label htmlFor="parentCategory" className="block text-[13px] font-medium text-[var(--primary-text-color)] mb-1">
                    {t("lang_Parent_Portfolio_Category") || "Parent Portfolio Category"}
                  </label>
                  <SelectComponent
                    name="selectedParentCategory"
                    placeholder="Select parent category"
                    value={selectedParentCategory}
                    options={[
                      { label: "-- None (Top Level) --", value: "" },
                      ...availableParentCategories.map((cat) => ({
                        value: cat._id || cat.id,
                        label: cat.parent_category || cat.portfoliocategory_name || cat.categoryName || "-",
                      })),
                    ]}
                    onChange={(e) => setSelectedParentCategory(e.target.value)}
                    isClearable={false}
                  />
                </div>

                {/* Category Description */}
                <div>
                  <label htmlFor="description" className="block text-[13px] font-medium text-[var(--primary-text-color)] mb-1">
                    {t("lang_description") || "Description"}
                  </label>
                  <RichTextEditor
                    htmlData={description}
                    setHtmlData={(val) => setDescription(val)}
                    uplaodImageFile={uploadTextEditorImage}
                  />
                  <div className="w-full flex justify-end mt-1">
                    <p className="text-xs text-gray-400">
                      {`${description?.split(/\s+/)?.filter(Boolean)?.length || 0}/280 words`}
                    </p>
                  </div>
                </div>
              </DetailsComponent>

              {/* Card 2: Search Engine Listing */}
              <DetailsComponent
                title={t("lang_search_engine_listing") || "Search engine listing"}
                subtitle={t("lang_search_engine_listing_detail") || "Add a title and description to see how this category might appear in a search engine listing"}
                open={true}
                contentClassName="px-4 sm:px-[25px] pb-4 sm:pb-[25px]"
              >
                <div className="mb-4">
                  <AiTextGenerate
                    fieldLabel={t("lang_meta_title") || "Meta Title"}
                    label={t("lang_get_meta_title") || "Generate"}
                    tooltip={t("lang_meta_tooltip", { type: "title" }) || "Generate SEO Title"}
                    onGenerate={() => generateMetaData(description || portfoliocategory_name, "title")}
                  />
                  <InputComponent
                    id="Meta Tag Title"
                    name="meta_tag_title"
                    value={meta_tag_title}
                    onChange={HandleInput}
                    placeholder="Enter meta title"
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p className={`text-xs mt-0 ${(meta_tag_title?.length || 0) > 60 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                      {`${meta_tag_title?.length || 0}/60`}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <AiTextGenerate
                    fieldLabel={t("lang_meta_description") || "Meta Description"}
                    label={t("lang_get_meta_description") || "Generate"}
                    tooltip={t("lang_meta_tooltip", { type: "description" }) || "Generate SEO Description"}
                    onGenerate={() => generateMetaData(description, "description")}
                  />
                  <textarea
                    className="w-full h-24 block px-3 py-2 border border-[var(--border-color)] rounded-lg focus:outline-none focus:border-[var(--focus-border-color)] bg-[var(--secondary-bg-color)] text-[var(--primary-text-color)] text-sm"
                    id="Meta Tag description"
                    name="meta_tag_description"
                    value={(meta_tag_description || '').replace(/<[^>]+>/g, '')}
                    onChange={HandleInput}
                    placeholder="Enter meta description"
                  />
                  <div className="flex w-full justify-end mt-1">
                    <p className={`text-xs mt-0 ${((meta_tag_description || '').replace(/<[^>]+>/g, '').length || 0) > 160 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                      {`${(meta_tag_description || '').replace(/<[^>]+>/g, '').length || 0}/160 words`}
                    </p>
                  </div>
                </div>

                <div>
                  <InputComponent
                    id="keyword"
                    name="keyword"
                    label={t("lang_meta_keywords") || "Keywords"}
                    value={keyword}
                    onChange={HandleInput}
                    placeholder="e.g. Portfolio showcase"
                  />
                </div>
              </DetailsComponent>
            </form>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="flex items-center justify-between w-full border-t border-[var(--border-color)] bg-[var(--secondary-bg-color)] p-3.5 px-6 shrink-0">
        <button
          type="button"
          onClick={handleBulkEditCancel}
          className="flex items-center text-xs font-medium text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors gap-1 cursor-pointer"
        >
          ← cancel
        </button>

        <div className="flex items-center gap-2">
          <ButtonComponent
            variant="primary"
            size="sm"
            loading={btnLoading}
            disabled={modifiedCount === 0 || btnLoading}
            onClick={handleBulkUpdate}
          >
            {modifiedCount > 0 ? `Save Changes (${modifiedCount})` : "Save Changes"}
          </ButtonComponent>
        </div>
      </div>

      <UnsavedChangesModal
        open={showUnsavedModal}
        onStay={handleStay}
        onLeave={handleConfirmLeave}
      />
    </div>
  );
};

export default BulkEditPortfolioCategory;
